import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { DockviewComponent } from 'dockview-core';
import 'dockview-core/dist/styles/dockview.css';

/**
 * DashDockLayout is a component that provides a flexible docking system for Dash applications,
 * allowing users to create, resize, and rearrange panels in a window manager style interface.
 * Supports floating groups, layout persistence, and renders Dash components inside panels.
 *
 * Panel positioning options:
 * - position: 'left' | 'right' | 'top' | 'bottom' | 'center' (default: 'center')
 * - size: number (pixels for positioned panels)
 * - visible: boolean (default: true)
 * - active: boolean (set as initially active panel)
 */
const DashDockLayout = (props) => {
    const {
        id,
        setProps,
        children,
        panels = [],
        layout = null,
        savedLayout = null,
        initialLayout = null,
        theme = 'dockview-theme-light',
        height = '600px',
        locked = false,
        disableDnd = false,
        disableFloatingGroups = false,
        hideBorders = false,
        hideCloseButtons = false,
        gap = 0,
        singleTabMode = 'default',
        showAddButton = true,
        loading_state = null,
    } = props;

    const containerRef = useRef(null);
    const dockviewApiRef = useRef(null);
    const [panelContainers, setPanelContainers] = useState({});
    const [isReady, setIsReady] = useState(false);
    const disposablesRef = useRef([]);
    const collapsedGroupsRef = useRef(new Set()); // Groups that were initially collapsed
    const collapsibleGroupsRef = useRef(new Set()); // Groups that can toggle collapse
    const expandedGroupsRef = useRef(new Set()); // Groups that are currently expanded (from collapsed state)
    const panelsRef = useRef(panels); // Keep panels in ref for event handlers
    const COLLAPSED_HEIGHT = 35; // Just enough for tab header

    // Update panelsRef when panels change
    useEffect(() => {
        panelsRef.current = panels;
    }, [panels]);

    // Create a mapping of panel IDs to children
    const childrenMap = useMemo(() => {
        const map = {};
        if (children) {
            // Handle both single child and array of children
            const childArray = Array.isArray(children) ? children : [children];
            childArray.forEach((child) => {
                if (child && child.props) {
                    // Try to get ID from various locations where Dash might put it
                    const childId = child.props.id ||
                                   child.props._dashprivate_layout?.props?.id ||
                                   child.key;
                    if (childId) {
                        map[childId] = child;
                    }
                }
            });
        }
        return map;
    }, [children]);

    // Initialize dockview - recreate when construction-time-only options change
    // These options can ONLY be set at construction time: disableDnd, disableFloatingGroups, hideBorders, singleTabMode
    useEffect(() => {
        if (!containerRef.current) return;

        // Store refs to panel containers
        const panelContainerRefs = {};

        const dockview = new DockviewComponent(containerRef.current, {
            createComponent: (options) => {
                const element = document.createElement('div');
                element.style.height = '100%';
                element.style.width = '100%';
                element.style.overflow = 'auto';
                element.style.boxSizing = 'border-box';
                element.className = 'dash-dock-panel-content';
                element.setAttribute('data-panel-id', options.id);

                // Store reference for React Portal
                panelContainerRefs[options.id] = element;

                return {
                    element,
                    init: (params) => {
                        // Panel is fully initialized and element is in DOM
                        // Update state to trigger portal creation
                        setPanelContainers(prev => ({
                            ...prev,
                            [options.id]: element
                        }));
                    },
                    dispose: () => {
                        // Remove from state when panel is disposed
                        setPanelContainers(prev => {
                            const next = { ...prev };
                            delete next[options.id];
                            return next;
                        });
                    }
                };
            },
            disableFloatingGroups,
            hideBorders,
            locked,
            disableDnd,
            gap,
            singleTabMode,
        });

        dockviewApiRef.current = dockview;

        // Setup event listeners
        const layoutDisposable = dockview.onDidLayoutChange(() => {
            if (setProps) {
                try {
                    setProps({ layout: dockview.toJSON() });
                } catch (err) {
                    console.error('Failed to serialize layout:', err);
                }
            }
        });
        disposablesRef.current.push(layoutDisposable);

        const activePanelDisposable = dockview.onDidActivePanelChange((event) => {
            if (setProps) {
                setProps({ activePanel: event?.panel?.id || null });
            }

            // When switching to a different tab in a collapsed collapsible group, expand it
            if (event?.panel?.group) {
                const group = event.panel.group;
                const groupId = group.id;

                // Only expand if this group is collapsible and currently collapsed
                if (collapsibleGroupsRef.current.has(groupId) && !expandedGroupsRef.current.has(groupId)) {
                    // Expand the group
                    const panelConfig = panelsRef.current.find(p =>
                        group.panels.some(gp => gp.id === p.id)
                    );
                    const expandedHeight = panelConfig?.size || 200;

                    if (group.api && group.api.setSize) {
                        group.api.setSize({ height: expandedHeight });
                    }
                    if (group.element) {
                        group.element.classList.remove('dv-group-collapsed');
                    }
                    expandedGroupsRef.current.add(groupId);
                }
            }
        });
        disposablesRef.current.push(activePanelDisposable);

        const addPanelDisposable = dockview.onDidAddPanel((panel) => {
            if (setProps) {
                setProps({
                    panelAdded: { id: panel.id, title: panel.title, timestamp: Date.now() },
                    panelCount: dockview.totalPanels
                });
            }
        });
        disposablesRef.current.push(addPanelDisposable);

        const removePanelDisposable = dockview.onDidRemovePanel((panel) => {
            if (setProps) {
                setProps({
                    panelRemoved: { id: panel.id, title: panel.title, timestamp: Date.now() },
                    panelCount: dockview.totalPanels
                });
            }
        });
        disposablesRef.current.push(removePanelDisposable);

        // Load initial panels with positioning support
        // If initialLayout is provided, use it directly
        if (initialLayout && initialLayout.grid) {
            try {
                dockview.fromJSON(initialLayout);
                console.log('[DashDockLayout] Loaded initialLayout');
            } catch (err) {
                console.error('[DashDockLayout] Failed to load initialLayout:', err);
            }
        } else if (panels.length > 0) {
            // Build layout from panels array with position support
            // Position mapping: left, right, top, bottom, center
            // Direction mapping for dockview: left, right, above, below, within

            // Sort panels by position priority: left, top, center, bottom, right
            const positionPriority = { left: 0, top: 1, center: 2, bottom: 3, right: 4 };
            const sortedPanels = [...panels]
                .filter(p => p.visible !== false) // Skip hidden panels
                .sort((a, b) => {
                    const posA = positionPriority[a.position] ?? 2; // default center
                    const posB = positionPriority[b.position] ?? 2;
                    return posA - posB;
                });

            // Group panels by position for tabbing
            const panelsByPosition = {};
            sortedPanels.forEach(panel => {
                const pos = panel.position || 'center';
                if (!panelsByPosition[pos]) {
                    panelsByPosition[pos] = [];
                }
                panelsByPosition[pos].push(panel);
            });

            // Track created groups by position for reference
            const groupsByPosition = {};
            let initialActivePanel = null;

            // Create panels in order: left, top, center, bottom, right
            ['left', 'top', 'center', 'bottom', 'right'].forEach(position => {
                const panelsInPosition = panelsByPosition[position] || [];

                panelsInPosition.forEach((panel, idx) => {
                    const panelId = panel.id;
                    const options = {
                        id: panelId,
                        component: 'default',
                        title: panel.title || panelId
                    };

                    // Check if this panel should be active
                    if (panel.active) {
                        initialActivePanel = panelId;
                    }

                    // Determine positioning
                    if (dockview.panels.length === 0) {
                        // First panel - no positioning needed
                        dockview.addPanel(options);
                        groupsByPosition[position] = panelId;
                    } else if (idx > 0 && groupsByPosition[position]) {
                        // Same position group - add as tab (within)
                        options.position = {
                            referencePanel: groupsByPosition[position],
                            direction: 'within'
                        };
                        dockview.addPanel(options);
                    } else {
                        // Different position - need to split
                        // Find a reference panel to position relative to
                        let refPanel = null;
                        let direction = 'right';

                        if (position === 'left') {
                            // Position left of center/first panel
                            refPanel = groupsByPosition['center'] || dockview.panels[0]?.id;
                            direction = 'left';
                        } else if (position === 'right') {
                            // Position right of center
                            refPanel = groupsByPosition['center'] || dockview.panels[0]?.id;
                            direction = 'right';
                        } else if (position === 'top') {
                            // Position above center
                            refPanel = groupsByPosition['center'] || dockview.panels[0]?.id;
                            direction = 'above';
                        } else if (position === 'bottom') {
                            // Position below center or left
                            refPanel = groupsByPosition['center'] || groupsByPosition['left'] || dockview.panels[0]?.id;
                            direction = 'below';
                        } else {
                            // Center - position right of left if exists
                            if (groupsByPosition['left']) {
                                refPanel = groupsByPosition['left'];
                                direction = 'right';
                            }
                        }

                        if (refPanel) {
                            options.position = {
                                referencePanel: refPanel,
                                direction: direction
                            };
                        }

                        dockview.addPanel(options);
                        groupsByPosition[position] = panelId;
                    }
                });
            });

            // Set initial active panel after all panels are created
            if (initialActivePanel) {
                setTimeout(() => {
                    const panel = dockview.panels.find(p => p.id === initialActivePanel);
                    if (panel && panel.api) {
                        panel.api.setActive();
                        if (setProps) {
                            setProps({ activePanel: initialActivePanel });
                        }
                    }
                }, 50);
            }

            // Apply sizes to groups if specified
            // Note: This requires accessing the grid API which may need adjustment
            setTimeout(() => {
                panels.forEach(panel => {
                    if (panel.size && panel.visible !== false) {
                        const dockPanel = dockview.panels.find(p => p.id === panel.id);
                        if (dockPanel && dockPanel.group) {
                            try {
                                // Try to set the group size
                                const group = dockPanel.group;
                                if (group.api && group.api.setSize) {
                                    const isHorizontal = panel.position === 'left' || panel.position === 'right';
                                    group.api.setSize({
                                        width: isHorizontal ? panel.size : undefined,
                                        height: !isHorizontal ? panel.size : undefined
                                    });
                                }
                            } catch (err) {
                                console.log('[DashDockLayout] Could not set panel size:', err);
                            }
                        }
                    }
                });
            }, 100);

            // Helper function to toggle collapse state
            const toggleGroupCollapse = (group) => {
                const groupId = group.id;
                if (!collapsibleGroupsRef.current.has(groupId)) return;

                const isExpanded = expandedGroupsRef.current.has(groupId);

                if (isExpanded) {
                    // Collapse it
                    if (group.api && group.api.setSize) {
                        group.api.setSize({ height: COLLAPSED_HEIGHT });
                    }
                    if (group.element) {
                        group.element.classList.add('dv-group-collapsed');
                    }
                    expandedGroupsRef.current.delete(groupId);
                } else {
                    // Expand it
                    const panelConfig = panelsRef.current.find(p =>
                        group.panels.some(gp => gp.id === p.id)
                    );
                    const expandedHeight = panelConfig?.size || 200;

                    if (group.api && group.api.setSize) {
                        group.api.setSize({ height: expandedHeight });
                    }
                    if (group.element) {
                        group.element.classList.remove('dv-group-collapsed');
                    }
                    expandedGroupsRef.current.add(groupId);
                }
            };

            // Helper function to set up tab click handlers for collapsible groups
            const setupTabClickHandlers = (group) => {
                const groupId = group.id;
                if (!collapsibleGroupsRef.current.has(groupId)) return;

                const groupElement = group.element;
                if (!groupElement) return;

                // Find the tabs container
                const tabsContainer = groupElement.querySelector('.dv-tabs-container');
                if (!tabsContainer) return;

                // Add click handler to tabs container (event delegation)
                tabsContainer.addEventListener('click', (e) => {
                    // Find the clicked tab
                    const tab = e.target.closest('.dv-tab');
                    if (!tab) return;

                    // Check if clicking on the currently active tab
                    const isActiveTab = tab.classList.contains('dv-tab-active') ||
                                       tab.querySelector('.dv-tab-active') !== null ||
                                       tab.closest('.dv-tab')?.classList.contains('dv-activegroup-visiblepanel-tab');

                    // If group is expanded and clicking active tab, collapse
                    if (expandedGroupsRef.current.has(groupId) && isActiveTab) {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleGroupCollapse(group);
                    }
                    // If group is collapsed, the onDidActivePanelChange will expand it
                }, true); // Use capture to get event before dockview
            };

            // Helper to apply group styling based on panel positions
            const applyGroupStyling = (group) => {
                const groupElement = group.element;
                if (!groupElement) return;

                // Check all panels in group to determine position
                for (const panel of group.panels) {
                    const panelConfig = panelsRef.current.find(p => p.id === panel.id);
                    if (panelConfig) {
                        // Mark bottom-positioned groups
                        if (panelConfig.position === 'bottom') {
                            groupElement.classList.add('dv-bottom-panel');
                        }
                        break; // Use first panel's position
                    }
                }
            };

            // Apply CSS classes for collapsed panels
            setTimeout(() => {
                dockview.groups.forEach(group => {
                    const panelInGroup = group.panels[0];
                    if (!panelInGroup) return;

                    // Find the config for the first panel in this group
                    const panelConfig = panels.find(p => p.id === panelInGroup.id);
                    if (!panelConfig) return;

                    const groupElement = group.element;
                    if (!groupElement) return;

                    // Mark bottom-positioned groups
                    if (panelConfig.position === 'bottom') {
                        groupElement.classList.add('dv-bottom-panel');
                    }

                    // Apply collapsed state if specified
                    if (panelConfig.collapsed) {
                        groupElement.classList.add('dv-group-collapsed');
                        collapsedGroupsRef.current.add(group.id);
                        collapsibleGroupsRef.current.add(group.id); // Mark as toggleable
                        // Note: Don't add to expandedGroupsRef - starts collapsed

                        // Set minimal height for collapsed panel
                        if (group.api && group.api.setSize) {
                            group.api.setSize({ height: COLLAPSED_HEIGHT });
                        }

                        // Set up click handlers for toggle
                        setupTabClickHandlers(group);
                    }
                });
            }, 150);

            // Listen for group changes to re-apply styling
            const groupAddedDisposable = dockview.onDidAddGroup((group) => {
                // Delay to let DOM settle
                setTimeout(() => {
                    applyGroupStyling(group);
                }, 50);
            });
            disposablesRef.current.push(groupAddedDisposable);

            // Listen for layout changes to re-apply vertical tab styling
            const layoutChangeDisposable = dockview.onDidLayoutChange(() => {
                // Re-apply vertical tabs class to left-positioned groups after layout changes
                setTimeout(() => {
                    dockview.groups.forEach(group => {
                        applyGroupStyling(group);
                    });
                }, 50);
            });
            disposablesRef.current.push(layoutChangeDisposable);
        }

        // Update panel count
        if (setProps) {
            setProps({ panelCount: dockview.totalPanels });
        }

        setIsReady(true);

        return () => {
            disposablesRef.current.forEach(d => d.dispose());
            disposablesRef.current = [];
            setPanelContainers({});
            dockview.dispose();
            dockviewApiRef.current = null;
            setIsReady(false);
        };
    // These options require recreation: disableDnd, disableFloatingGroups, hideBorders, singleTabMode
    // Note: theme is NOT included - it's just a CSS class that React handles automatically
    }, [disableDnd, disableFloatingGroups, hideBorders, singleTabMode]);

    // Update dockview options dynamically via updateOptions
    // These update the internal options object which dockview checks during operations
    useEffect(() => {
        if (dockviewApiRef.current) {
            dockviewApiRef.current.updateOptions({
                locked,
                gap,
                disableDnd,
                disableFloatingGroups,
            });
        }
    }, [locked, gap, disableDnd, disableFloatingGroups]);

    // Load saved layout when prop changes
    useEffect(() => {
        if (dockviewApiRef.current && savedLayout) {
            try {
                dockviewApiRef.current.fromJSON(savedLayout);
            } catch (err) {
                console.error('Failed to load layout:', err);
            }
        }
    }, [savedLayout]);

    // Handle loadLayoutTrigger - allows loading layout with timestamp to force re-trigger
    useEffect(() => {
        if (dockviewApiRef.current && props.loadLayoutTrigger) {
            const layoutData = props.loadLayoutTrigger.layout || props.loadLayoutTrigger;
            if (layoutData && layoutData.grid) {
                try {
                    dockviewApiRef.current.fromJSON(layoutData);
                    if (setProps) {
                        setProps({ panelCount: dockviewApiRef.current.totalPanels });
                    }
                } catch (err) {
                    console.error('Failed to load layout from trigger:', err);
                }
            }
        }
    }, [props.loadLayoutTrigger]);

    // Handle addPanelTrigger
    useEffect(() => {
        if (dockviewApiRef.current && props.addPanelTrigger) {
            const { id: panelId, title, component = 'default', position, size } = props.addPanelTrigger;
            if (panelId) {
                const options = {
                    id: panelId,
                    component,
                    title: title || panelId
                };

                // Support position object for positioning relative to other panels
                // e.g., {referencePanel: 'panel-id', direction: 'below'}
                if (position) {
                    options.position = position;
                }

                const newPanel = dockviewApiRef.current.addPanel(options);

                // Apply size if specified
                if (size && newPanel && newPanel.group) {
                    setTimeout(() => {
                        try {
                            const group = newPanel.group;
                            if (group.api && group.api.setSize) {
                                // Determine if horizontal or vertical based on direction
                                const direction = position?.direction;
                                const isHorizontal = direction === 'left' || direction === 'right';
                                group.api.setSize({
                                    width: isHorizontal ? size : undefined,
                                    height: !isHorizontal ? size : undefined
                                });
                            }
                        } catch (err) {
                            console.log('[DashDockLayout] Could not set panel size:', err);
                        }
                    }, 50);
                }
            }
        }
    }, [props.addPanelTrigger]);

    // Handle removePanelTrigger
    useEffect(() => {
        if (dockviewApiRef.current && props.removePanelTrigger) {
            const panel = dockviewApiRef.current.getPanel(props.removePanelTrigger);
            if (panel) {
                dockviewApiRef.current.removePanel(panel);
            }
        }
    }, [props.removePanelTrigger]);

    // Handle clearTrigger
    useEffect(() => {
        if (dockviewApiRef.current && props.clearTrigger) {
            dockviewApiRef.current.clear();
            if (setProps) {
                setProps({ clearTrigger: false, panelCount: 0 });
            }
        }
    }, [props.clearTrigger]);

    // Handle activatePanelTrigger - programmatically activate/focus a panel
    useEffect(() => {
        if (dockviewApiRef.current && props.activatePanelTrigger) {
            const panelId = props.activatePanelTrigger.panelId || props.activatePanelTrigger;

            if (panelId) {
                // Find the panel in the panels array
                const panel = dockviewApiRef.current.panels.find(p => p.id === panelId);

                if (panel && panel.api) {
                    try {
                        panel.api.setActive();
                        if (setProps) {
                            setProps({ activePanel: panelId });
                        }
                    } catch (err) {
                        console.error('[DashDockLayout] Error activating panel:', err);
                    }
                }
            }
        }
    }, [props.activatePanelTrigger]);

    const handleAddPanel = useCallback(() => {
        if (!dockviewApiRef.current) return;

        const panelId = `panel-${Date.now()}`;
        dockviewApiRef.current.addPanel({
            id: panelId,
            component: 'default',
            title: `Panel ${dockviewApiRef.current.panels.length + 1}`
        });
    }, []);

    // Render portals for each panel - only when containers are in DOM
    const portals = useMemo(() => {
        if (!isReady) return [];

        return Object.entries(panelContainers).map(([panelId, container]) => {
            // Verify container is actually in the document
            if (!container || !document.body.contains(container)) {
                return null;
            }

            const child = childrenMap[panelId];
            if (child) {
                return ReactDOM.createPortal(
                    <div style={{ height: '100%', width: '100%', overflow: 'auto' }} key={panelId}>
                        {child}
                    </div>,
                    container
                );
            }
            // If no matching child, show placeholder
            return ReactDOM.createPortal(
                <div style={{ padding: '1rem', color: 'var(--dv-foreground-color, #333)' }} key={panelId}>
                    <p>Panel: {panelId}</p>
                    <p style={{ fontSize: '0.8em', opacity: 0.7 }}>No matching child component found</p>
                </div>,
                container
            );
        }).filter(Boolean);
    }, [panelContainers, childrenMap, isReady]);

    return (
        <div id={id} className={theme} style={{ width: '100%', height }}>
            {showAddButton && (
                <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                        onClick={handleAddPanel}
                        style={{
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            border: '1px solid var(--dv-separator-border, #ccc)',
                            borderRadius: '4px',
                            background: 'var(--dv-background-color, #fff)',
                            color: 'var(--dv-foreground-color, #333)'
                        }}
                    >
                        Add Panel
                    </button>
                </div>
            )}
            <div
                ref={containerRef}
                className={`${theme}${hideCloseButtons ? ' dv-hide-all-close-buttons' : ''}`}
                style={{
                    height: showAddButton ? 'calc(100% - 40px)' : '100%',
                    backgroundColor: 'var(--dv-background-color)',
                    border: '1px solid var(--dv-separator-border)'
                }}
            />
            {portals}
        </div>
    );
};

DashDockLayout.propTypes = {
    /**
     * The ID used to identify this component in Dash callbacks.
     */
    id: PropTypes.string.isRequired,

    /**
     * Dash components to render in panels. Each child should have an id matching a panel id.
     */
    children: PropTypes.node,

    /**
     * Initial panels to display. Each panel can have positioning options.
     * - id: (required) Unique panel identifier
     * - title: Display title for the tab
     * - position: 'left' | 'right' | 'top' | 'bottom' | 'center' (default: 'center')
     * - size: Initial size in pixels for positioned panels
     * - visible: Whether panel is visible on load (default: true)
     * - active: Whether this panel should be active/focused on load
     * - collapsed: Whether panel group starts collapsed (shows only tab header)
     */
    panels: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string,
        position: PropTypes.oneOf(['left', 'right', 'top', 'bottom', 'center']),
        size: PropTypes.number,
        visible: PropTypes.bool,
        active: PropTypes.bool,
        collapsed: PropTypes.bool
    })),

    /**
     * Currently active panel ID (read-only, updated by component)
     */
    activePanel: PropTypes.string,

    /**
     * Current layout configuration (read-only, updated by component)
     */
    layout: PropTypes.object,

    /**
     * Layout JSON to load. Set this to restore a previously saved layout.
     */
    savedLayout: PropTypes.object,

    /**
     * Initial layout configuration. Use this to define complex layouts.
     * This is a Dockview JSON layout object with grid, panels, and activeGroup.
     * Use the `layout` output prop to capture a layout and reuse it here.
     */
    initialLayout: PropTypes.object,

    /**
     * Theme class name ('dockview-theme-light' or 'dockview-theme-dark')
     */
    theme: PropTypes.string,

    /**
     * Height of the component (CSS value)
     */
    height: PropTypes.string,

    /**
     * Lock the entire layout (prevents all modifications)
     */
    locked: PropTypes.bool,

    /**
     * Disable drag-and-drop functionality
     */
    disableDnd: PropTypes.bool,

    /**
     * Disable floating groups
     */
    disableFloatingGroups: PropTypes.bool,

    /**
     * Hide borders between panels
     */
    hideBorders: PropTypes.bool,

    /**
     * Hide close buttons on all tabs. When true, users cannot close panels.
     */
    hideCloseButtons: PropTypes.bool,

    /**
     * Pixel gap between groups
     */
    gap: PropTypes.number,

    /**
     * Tab display mode ('default' or 'fullwidth')
     */
    singleTabMode: PropTypes.oneOf(['default', 'fullwidth']),

    /**
     * Show the Add Panel button
     */
    showAddButton: PropTypes.bool,

    /**
     * Last added panel info (read-only, updated by component)
     */
    panelAdded: PropTypes.object,

    /**
     * Last removed panel info (read-only, updated by component)
     */
    panelRemoved: PropTypes.object,

    /**
     * Total number of panels (read-only, updated by component)
     */
    panelCount: PropTypes.number,

    /**
     * Trigger to load a layout. Set to {layout, timestamp} to load a saved layout.
     * The timestamp ensures React sees each load as a new value.
     */
    loadLayoutTrigger: PropTypes.shape({
        layout: PropTypes.object.isRequired,
        timestamp: PropTypes.number
    }),

    /**
     * Trigger to add a panel. Set to {id, title} to add a new panel.
     * Supports position for placing relative to existing panels:
     * - position: {referencePanel: 'panel-id', direction: 'below'|'above'|'left'|'right'|'within'}
     * - size: Initial size in pixels (height for above/below, width for left/right)
     */
    addPanelTrigger: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string,
        component: PropTypes.string,
        position: PropTypes.shape({
            referencePanel: PropTypes.string.isRequired,
            direction: PropTypes.oneOf(['left', 'right', 'above', 'below', 'within'])
        }),
        size: PropTypes.number
    }),

    /**
     * Panel ID to remove. Set this to remove a panel.
     */
    removePanelTrigger: PropTypes.string,

    /**
     * Set to true to clear all panels
     */
    clearTrigger: PropTypes.bool,

    /**
     * Trigger to activate/switch to a specific panel tab.
     * Set to {panelId: string, timestamp?: number} to activate that panel.
     * The timestamp ensures React sees repeated activations as new values.
     */
    activatePanelTrigger: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            panelId: PropTypes.string.isRequired,
            timestamp: PropTypes.number
        })
    ]),

    /**
     * Dash-assigned callback that should be called to report property changes
     * to Dash, to make them available for callbacks.
     */
    setProps: PropTypes.func,

    /**
     * Dash loading state
     */
    loading_state: PropTypes.shape({
        is_loading: PropTypes.bool,
        prop_name: PropTypes.string,
        component_name: PropTypes.string,
    }),
};

export default DashDockLayout;
